import AsyncStorage from '@react-native-async-storage/async-storage';
import * as SecureStore from 'expo-secure-store';

import type { AuthTokens } from '@store/auth/auth.types';
import { Platform } from 'react-native';

export const AUTH_STORAGE_KEY = 'auth_session_v2';
const isWeb = Platform.OS === 'web';

type StoredAuthSession = {
  refreshToken: string;
};

const canUseSecureStore = async (): Promise<boolean> => {
  if (isWeb) return false;

  try {
    return await SecureStore.isAvailableAsync();
  } catch (error) {
    console.error(
      '[AUTH_STORAGE] SecureStore availability check failed:',
      error,
    );
    return false;
  }
};

const write = async (key: string, value: string): Promise<void> => {
  if (isWeb) {
    sessionStorage.setItem(key, value);
    return;
  }

  const useSecureStore = await canUseSecureStore();
  if (useSecureStore) {
    await SecureStore.setItemAsync(key, value);
  } else {
    await AsyncStorage.setItem(key, value);
  }
};

const read = async (key: string): Promise<string | null> => {
  if (isWeb) {
    return sessionStorage.getItem(key);
  }

  const useSecureStore = await canUseSecureStore();
  return useSecureStore
    ? await SecureStore.getItemAsync(key)
    : await AsyncStorage.getItem(key);
};

const remove = async (key: string): Promise<void> => {
  if (isWeb) {
    sessionStorage.removeItem(key);
    return;
  }

  const useSecureStore = await canUseSecureStore();
  if (useSecureStore) {
    await SecureStore.deleteItemAsync(key);
  } else {
    await AsyncStorage.removeItem(key);
  }
};

/**
 * Persist only what is needed for silent re-auth:
 * - refreshToken
 *
 * We DO NOT persist idToken/accessToken to avoid:
 * - 2048 bytes SecureStore limit warnings
 * - storing large/JWT tokens on web storage
 */
export const saveAuthSession = async (tokens: AuthTokens): Promise<void> => {
  try {
    if (!tokens.refreshToken) return;

    const payload: StoredAuthSession = {
      refreshToken: tokens.refreshToken,
    };

    await write(AUTH_STORAGE_KEY, JSON.stringify(payload));
  } catch (error) {
    console.error('[AUTH_STORAGE] Failed to persist auth session:', error);
  }
};

export const getStoredAuthSession = async (): Promise<Pick<
  AuthTokens,
  'refreshToken'
> | null> => {
  try {
    const data = await read(AUTH_STORAGE_KEY);
    if (!data) return null;

    const parsed = JSON.parse(data) as Partial<StoredAuthSession>;
    if (!parsed.refreshToken) return null;

    return { refreshToken: parsed.refreshToken };
  } catch (error) {
    console.error('[AUTH_STORAGE] Failed to read auth session:', error);
    return null;
  }
};

export const clearAuthSession = async (): Promise<void> => {
  try {
    await remove(AUTH_STORAGE_KEY);
  } catch (error) {
    console.error('[AUTH_STORAGE] Failed to clear auth session:', error);
  }
};
